import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import brandsJson from '@/content/brands.json';
import categoriesJson from '@/content/categories.json';
import faqJson from '@/content/faq.json';
import productsJson from '@/content/products.json';
import settingsJson from '@/content/settings.json';
import type { FaqItem } from '@/data/faq';
import { toSlug } from '@/lib/slug';
import { readStorage, removeStorage, writeStorage } from '@/lib/storage';
import type { Brand, Category, Product } from '@/types/catalog';

import {
  AdminContext,
  type AdminValue,
  type LoadStatus,
  type PublishResult,
} from './admin.context';
import {
  CONTENT_LABELS,
  CONTENT_PATHS,
  type AdminContent,
  type ContentKey,
  type SiteSettings,
} from './admin.types';
import { resizeImage } from './resizeImage';

/**
 * v2 guarda junto de cada parte o SHA do arquivo sobre o qual ela foi editada.
 * O v1 não tinha isso e é ignorado: sem saber a base, não há como dizer se
 * publicar aquele rascunho apagaria algo.
 */
const DRAFT_KEY = 'brucepets.admin.draft.v2';
const LEGACY_DRAFT_KEY = 'brucepets.admin.draft.v1';

type Shas = Partial<Record<string, string>>;

interface Draft {
  base: Shas;
  parts: Partial<AdminContent>;
}

interface RemoteContent {
  files: Record<string, { sha: string; content: string }>;
  /** Fotos em src/assets/products/ no momento da leitura. */
  images?: string[];
}

interface PendingImage {
  base64: string;
  previewUrl: string;
}

/** Nomes de foto citados no catálogo (arquivos locais, não URLs). */
function referencedImages(products: Product[]): Set<string> {
  const names = new Set<string>();
  for (const product of products) {
    for (const reference of product.images ?? (product.image ? [product.image] : [])) {
      names.add(reference.trim().toLowerCase());
    }
  }
  return names;
}

const KEYS = Object.keys(CONTENT_PATHS) as ContentKey[];

/** O conteúdo que veio no build — só a forma inicial, antes de ler o GitHub. */
function bundledContent(): AdminContent {
  return {
    products: productsJson as Product[],
    brands: brandsJson as Brand[],
    categories: categoriesJson as Category[],
    faq: faqJson as FaqItem[],
    settings: settingsJson as SiteSettings,
  };
}

/** Cópia profunda simples: o conteúdo é JSON puro, sem datas nem classes. */
function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function serialise(value: unknown): string {
  return `${JSON.stringify(value, null, 2)}\n`;
}

export function AdminProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<LoadStatus>('loading');
  const [attempt, setAttempt] = useState(0);
  const [published, setPublished] = useState<AdminContent>(bundledContent);
  const [content, setContent] = useState<AdminContent>(bundledContent);
  const [baseShas, setBaseShas] = useState<Shas>({});
  const [droppedDraftKeys, setDroppedDraftKeys] = useState<ContentKey[]>([]);
  const [knownImages, setKnownImages] = useState<ReadonlySet<string>>(new Set());
  // Fotos ficam só na memória até publicar: em base64 elas estourariam o
  // limite do localStorage. Se a página recarregar antes, a conferência
  // aponta a foto que falta e pede para enviar de novo.
  const [pending, setPending] = useState<Record<string, PendingImage>>({});

  /**
   * O ponto de partida é o que está no GitHub agora, não o JSON do build.
   *
   * O bundle fica para trás por cerca de um minuto depois de cada publicação,
   * e indefinidamente se outra pessoa publicar. Editar sobre ele e publicar
   * sobrescrevia o arquivo inteiro com a versão velha — foi assim que
   * produtos e marcas cadastrados sumiram.
   */
  useEffect(() => {
    let cancelled = false;

    async function load() {
      setStatus('loading');
      try {
        const response = await fetch('/api/content', { headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error(String(response.status));
        const remote = (await response.json()) as RemoteContent;
        if (cancelled) return;

        const next = bundledContent();
        const shas: Shas = {};
        for (const key of KEYS) {
          const file = remote.files[CONTENT_PATHS[key]];
          if (!file) continue;
          next[key] = JSON.parse(file.content) as never;
          shas[CONTENT_PATHS[key]] = file.sha;
        }

        // Rascunho só volta se foi feito sobre a versão que está no ar. Feito
        // sobre uma versão anterior, publicá-lo desfaria o que veio depois.
        removeStorage(LEGACY_DRAFT_KEY);
        const draft = readStorage<Draft | null>(DRAFT_KEY, null);
        const restored = clone(next);
        const dropped: ContentKey[] = [];
        if (draft && typeof draft === 'object' && draft.parts && draft.base) {
          for (const key of KEYS) {
            if (draft.parts[key] === undefined) continue;
            if (draft.base[CONTENT_PATHS[key]] === shas[CONTENT_PATHS[key]]) {
              restored[key] = draft.parts[key] as never;
            } else {
              dropped.push(key);
            }
          }
        }

        setPublished(next);
        setContent(restored);
        setBaseShas(shas);
        setKnownImages(new Set((remote.images ?? []).map((name) => name.toLowerCase())));
        setDroppedDraftKeys(dropped);
        setStatus('ready');
      } catch {
        if (!cancelled) setStatus('error');
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [attempt]);

  const reload = useCallback(() => setAttempt((value) => value + 1), []);

  const dirtyKeys = useMemo(
    () => KEYS.filter((key) => serialise(content[key]) !== serialise(published[key])),
    [content, published],
  );

  /**
   * Guarda no navegador SÓ o que está diferente do publicado, com a versão
   * de base de cada parte. O resto sempre acompanha o que está no ar.
   */
  useEffect(() => {
    if (status !== 'ready') return;
    if (dirtyKeys.length === 0) {
      removeStorage(DRAFT_KEY);
      return;
    }

    const draft: Draft = { base: {}, parts: {} };
    for (const key of dirtyKeys) {
      draft.parts[key] = content[key] as never;
      draft.base[CONTENT_PATHS[key]] = baseShas[CONTENT_PATHS[key]];
    }
    writeStorage(DRAFT_KEY, draft);
  }, [status, content, dirtyKeys, baseShas]);

  const update = useCallback(<K extends ContentKey>(key: K, value: AdminContent[K]) => {
    setContent((current) => ({ ...current, [key]: value }));
  }, []);

  const discard = useCallback(() => {
    removeStorage(DRAFT_KEY);
    setContent(clone(published));
  }, [published]);

  const addImage = useCallback(async (file: File, baseName: string) => {
    const resized = await resizeImage(file);
    // Sufixo aleatório: o servidor recusa sobrescrever uma foto existente, e
    // trocar a foto de um produto nunca deve mudar a de outro.
    const suffix = Math.random().toString(36).slice(2, 7);
    const name = `${toSlug(baseName).slice(0, 60) || 'produto'}-${suffix}.${resized.extension}`;
    setPending((current) => ({
      ...current,
      [name]: { base64: resized.base64, previewUrl: resized.previewUrl },
    }));
    return name;
  }, []);

  const publish = useCallback(async (): Promise<PublishResult> => {
    // Fotografa o que vai ser enviado: edições feitas enquanto a requisição
    // está no ar continuam pendentes em vez de serem dadas como publicadas.
    const sent = dirtyKeys.map((key) => [key, clone(content[key])] as const);
    // Só sobem as fotos que algum produto ainda usa: enviar e depois remover
    // do produto não deixa arquivo órfão no repositório.
    const used = referencedImages(content.products);
    const images: Record<string, string> = {};
    for (const [name, image] of Object.entries(pending)) {
      if (used.has(name) && !knownImages.has(name)) images[name] = image.base64;
    }
    const files: Record<string, string> = {};
    const shas: Shas = {};
    for (const [key, value] of sent) {
      files[CONTENT_PATHS[key]] = serialise(value);
      shas[CONTENT_PATHS[key]] = baseShas[CONTENT_PATHS[key]];
    }

    let response: Response;
    try {
      response = await fetch('/api/publish', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          files,
          images,
          baseShas: shas,
          message: `atualiza ${sent.map(([key]) => CONTENT_LABELS[key].toLowerCase()).join(', ')}`,
        }),
      });
    } catch {
      return {
        ok: false,
        conflict: false,
        error: 'Falha de conexão. Verifique a internet e tente de novo.',
      };
    }

    const payload = (await response.json().catch(() => ({}))) as {
      error?: string;
      sha?: string;
      url?: string;
      shas?: Record<string, string>;
    };

    if (!response.ok) {
      return {
        ok: false,
        conflict: response.status === 409,
        error: payload.error ?? `Erro ${response.status}.`,
      };
    }

    setPublished((current) => {
      const next = { ...current };
      for (const [key, value] of sent) next[key] = value as never;
      return next;
    });
    setBaseShas((current) => ({ ...current, ...payload.shas }));
    const uploaded = Object.keys(images);
    if (uploaded.length > 0) {
      setKnownImages((current) => new Set([...current, ...uploaded]));
    }

    return { ok: true, sha: payload.sha ?? '', ...(payload.url ? { url: payload.url } : {}) };
  }, [content, dirtyKeys, baseShas, pending, knownImages]);

  const pendingImages = useMemo(
    () =>
      Object.fromEntries(Object.entries(pending).map(([name, image]) => [name, image.previewUrl])),
    [pending],
  );

  const value = useMemo<AdminValue>(
    () => ({
      status,
      reload,
      content,
      dirtyKeys,
      isDirty: dirtyKeys.length > 0,
      droppedDraftKeys,
      update,
      discard,
      publish,
      knownImages,
      pendingImages,
      addImage,
    }),
    [
      status,
      reload,
      content,
      dirtyKeys,
      droppedDraftKeys,
      update,
      discard,
      publish,
      knownImages,
      pendingImages,
      addImage,
    ],
  );

  return <AdminContext.Provider value={value}>{children}</AdminContext.Provider>;
}
